import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import TrainIcon from "@mui/icons-material/Train";
import ScienceIcon from "@mui/icons-material/Science";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckIcon from "@mui/icons-material/Check";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import EditNoteIcon from "@mui/icons-material/EditNote";
import GridViewIcon from "@mui/icons-material/GridView";
import ViewListIcon from "@mui/icons-material/ViewList";
import { GroupedVirtuoso, type GroupedVirtuosoHandle } from "react-virtuoso";
import { useAppData } from "store/appData";
import { useDatasetDetail } from "./DatasetDetailContext";
import DatasetImageFilterButton, {
  type FilterOption,
} from "./DatasetImageFilterButton";
import { useDatasetImageFilter } from "./useDatasetImageFilter";
import {
  imageHasFalsePositive,
  imageHasTruePositive,
} from "./imageWorkflowStatus";
import { useProjectLabelMap } from "../hooks/useDatasetViewerSettings";
import { DEFECT_BOX_COLOR, type DatasetImage } from "../api";
import t from "../translations";

// Images bucketed under their project, in the order each project first
// appears in the (image-id-ordered) list — a stable order that does not
// reshuffle as more images load.
interface ProjectGroup {
  projectId: number;
  images: DatasetImage[];
}

// Resolves ids to filter options via `nameOf`, alphabetically sorted —
// shared by the object-type and project filter option lists below.
function toSortedFilterOptions(
  ids: number[],
  nameOf: (id: number) => string,
): FilterOption[] {
  return ids
    .map((id) => ({ id, name: nameOf(id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export default function DatasetImageList() {
  const params = useParams<{ datasetId: string; imageId: string }>();
  const datasetId = params.datasetId ?? "";
  const activeImageId = params.imageId ? parseInt(params.imageId) : -1;

  const { objectTypes } = useAppData();
  const {
    datasetName,
    datasetTypeList,
    totalImageCount,
    projectCounts,
    detailBasePath,
    kind,
    viewMode,
    setViewMode,
    expandedProjects,
    toggleProject,
    imageClosed,
    setImageClosed,
    seenImageIds,
    markImageSeen,
  } = useDatasetDetail();
  const { filters, filteredImages, filterActive, updateFilters, search } =
    useDatasetImageFilter();

  const virtuosoRef = useRef<GroupedVirtuosoHandle>(null);
  const navigate = useNavigate();
  const [projectsExpanded, setProjectsExpanded] = useState(false);

  const projectLabelMap = useProjectLabelMap();

  // Full per-project image counts from the dataset detail, so an accordion
  // header shows the real total even before every page has loaded.
  const projectTotalMap = useMemo(
    () => new Map(projectCounts.map(({ id, count }) => [id, count])),
    [projectCounts],
  );

  // Object types the dataset declares (its type_list), resolved to names for
  // the filter popover and sorted alphabetically.
  const objectTypeOptions = useMemo<FilterOption[]>(
    () =>
      toSortedFilterOptions(
        datasetTypeList,
        (id) => objectTypes[id]?.en_name ?? t.UnknownObjectType(id),
      ),
    [datasetTypeList, objectTypes],
  );

  // Projects present in this dataset, resolved to labels for the filter
  // popover and sorted alphabetically.
  const projectFilterOptions = useMemo<FilterOption[]>(
    () =>
      toSortedFilterOptions(
        projectCounts.map(({ id }) => id),
        (id) => projectLabelMap.get(id) ?? t.UnknownProject(id),
      ),
    [projectCounts, projectLabelMap],
  );

  const groups = useMemo<ProjectGroup[]>(() => {
    const order: number[] = [];
    const byProject = new Map<number, DatasetImage[]>();
    for (const image of filteredImages) {
      let bucket = byProject.get(image.project_id);
      if (!bucket) {
        bucket = [];
        byProject.set(image.project_id, bucket);
        order.push(image.project_id);
      }
      bucket.push(image);
    }
    return order.map((projectId) => ({
      projectId,
      images: byProject.get(projectId) as DatasetImage[],
    }));
  }, [filteredImages]);

  // Items of expanded groups only, concatenated in group order — this is the
  // sequence GroupedVirtuoso's flat `itemContent` index addresses.
  const flatImages = useMemo(
    () =>
      groups.flatMap((group) =>
        expandedProjects.has(group.projectId) ? group.images : [],
      ),
    [groups, expandedProjects],
  );

  // A collapsed project contributes 0 items, so GroupedVirtuoso renders just
  // its header.
  const groupCounts = useMemo(
    () =>
      groups.map((group) =>
        expandedProjects.has(group.projectId) ? group.images.length : 0,
      ),
    [groups, expandedProjects],
  );

  useEffect(() => {
    markImageSeen(activeImageId);
  }, [activeImageId, markImageSeen]);

  useEffect(() => {
    // Crops mode lets the grid fill the right side; skip the auto-select.
    if (viewMode !== "image") return;
    // The user explicitly closed the image — don't re-open it from under them.
    if (imageClosed) return;
    if (activeImageId >= 0 || !filteredImages.length) return;
    navigate(
      {
        pathname: `${detailBasePath}/${datasetId}/${filteredImages[0].id}`,
        search,
      },
      { replace: true },
    );
  }, [
    viewMode,
    activeImageId,
    filteredImages,
    navigate,
    detailBasePath,
    datasetId,
    search,
    imageClosed,
  ]);

  // Keep the list scrolled to the active image. If its project accordion is
  // collapsed, bring the project header into view rather than forcing it open.
  useEffect(() => {
    if (activeImageId < 0) return;
    const itemIndex = flatImages.findIndex((img) => img.id === activeImageId);
    if (itemIndex >= 0) {
      virtuosoRef.current?.scrollToIndex({ index: itemIndex, align: "center" });
      return;
    }
    const activeImage = filteredImages.find((img) => img.id === activeImageId);
    if (!activeImage) return;
    const groupIndex = groups.findIndex(
      (group) => group.projectId === activeImage.project_id,
    );
    if (groupIndex >= 0) {
      virtuosoRef.current?.scrollToIndex({ groupIndex, align: "center" });
    }
  }, [activeImageId, flatImages, filteredImages, groups]);

  useEffect(() => {
    if (activeImageId < 0 || !filteredImages.length) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const target = event.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT"
      ) {
        return;
      }

      const index = filteredImages.findIndex((img) => img.id === activeImageId);
      if (index < 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        const next = filteredImages[index + 1];
        if (next) {
          navigate({
            pathname: `${detailBasePath}/${datasetId}/${next.id}`,
            search,
          });
        }
      } else {
        event.preventDefault();
        const prev = filteredImages[index - 1];
        if (prev) {
          navigate({
            pathname: `${detailBasePath}/${datasetId}/${prev.id}`,
            search,
          });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeImageId,
    filteredImages,
    navigate,
    detailBasePath,
    datasetId,
    search,
  ]);

  const handleRowClick = (image: DatasetImage) => {
    setImageClosed(false);
    navigate({
      pathname: `${detailBasePath}/${datasetId}/${image.id}`,
      search,
    });
  };

  const hasProjects = projectCounts.length > 0;
  const imageCountText = datasetName
    ? t.ImageCount(filterActive ? filteredImages.length : totalImageCount)
    : t.LoadingImages;
  const projectCountText = t.ProjectCount(projectCounts.length);

  return (
    <Stack
      sx={{
        height: "100%",
        width: "100%",
        bgcolor: "background.paper",
      }}
      data-testid="datasetImageList.container"
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Stack
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
              cursor: hasProjects ? "pointer" : "default",
              userSelect: "none",
              color: "text.secondary",
            }}
            onClick={() => hasProjects && setProjectsExpanded((prev) => !prev)}
            data-testid="datasetImageList.projectCountToggle"
          >
            <Typography variant="caption">{imageCountText}</Typography>
            {hasProjects && (
              <>
                <Typography variant="caption">·</Typography>
                <Typography variant="caption">{projectCountText}</Typography>
                {projectsExpanded ? (
                  <ExpandLessIcon fontSize="inherit" />
                ) : (
                  <ExpandMoreIcon fontSize="inherit" />
                )}
              </>
            )}
          </Stack>
          <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 0.25 }}>
            <Tooltip
              title={viewMode === "crops" ? t.ViewModeList : t.ViewModeAllCrops}
              arrow
            >
              <IconButton
                size="small"
                onClick={() =>
                  setViewMode(viewMode === "crops" ? "image" : "crops")
                }
                color={viewMode === "crops" ? "primary" : "default"}
                data-testid="datasetImageList.viewToggle"
                aria-label={t.ToggleAllCropsView}
                aria-pressed={viewMode === "crops"}
              >
                {viewMode === "crops" ? (
                  <ViewListIcon fontSize="small" />
                ) : (
                  <GridViewIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
            <DatasetImageFilterButton
              filters={filters}
              filterActive={filterActive}
              updateFilters={updateFilters}
              objectTypeOptions={objectTypeOptions}
              projectOptions={projectFilterOptions}
            />
          </Stack>
        </Stack>
        {hasProjects && projectsExpanded && (
          <Box
            sx={{ mt: 0.5, pt: 0.5, maxHeight: "50vh", overflowY: "auto" }}
            data-testid="datasetImageList.projectBreakdown"
          >
            {projectCounts.map(({ id, count }, index) => {
              const pct =
                totalImageCount > 0
                  ? Math.round((count / totalImageCount) * 100)
                  : 0;
              return (
                <Stack
                  key={id}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    gap: 1,
                    px: 0.5,
                    py: 0.25,
                    fontSize: 11,
                    color: "text.secondary",
                    bgcolor: index % 2 === 0 ? "action.hover" : "transparent",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {projectLabelMap.get(id) ?? t.UnknownProject(id)}
                  </Box>
                  <Stack
                    sx={{
                      flexDirection: "row",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box component="span">{count.toLocaleString()}</Box>
                    <Box
                      component="span"
                      sx={{
                        color: "text.disabled",
                        fontSize: 10,
                        width: 32,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {pct}%
                    </Box>
                  </Stack>
                </Stack>
              );
            })}
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <GroupedVirtuoso
          ref={virtuosoRef}
          style={{ width: "100%", height: "100%" }}
          groupCounts={groupCounts}
          groupContent={(groupIndex) => {
            const group = groups[groupIndex];
            if (!group) return null;
            const expanded = expandedProjects.has(group.projectId);
            const label =
              projectLabelMap.get(group.projectId) ??
              t.UnknownProject(group.projectId);
            const count = filterActive
              ? group.images.length
              : (projectTotalMap.get(group.projectId) ?? group.images.length);
            return (
              <Stack
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={() => toggleProject(group.projectId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleProject(group.projectId);
                  }
                }}
                data-testid={`datasetImageList.projectAccordion.${group.projectId}`}
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  userSelect: "none",
                  bgcolor: "background.paper",
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {expanded ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {count.toLocaleString()}
                </Typography>
              </Stack>
            );
          }}
          itemContent={(index) => {
            const image = flatImages[index];
            if (!image) return null;
            const active = image.id === activeImageId;
            const hasTruePositive = imageHasTruePositive(
              image,
              kind,
              objectTypes,
            );
            const hasFalsePositive = imageHasFalsePositive(
              image,
              kind,
              objectTypes,
            );
            const objectWithoutDefect =
              kind === "defect" && image.has_object_without_defect;
            const seen = seenImageIds.has(image.id);
            return (
              <Stack
                sx={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.625,
                  mb: "1px",
                  cursor: active ? "default" : "pointer",
                  bgcolor: active ? "primary.main" : "transparent",
                  color: active ? "primary.contrastText" : "text.primary",
                  fontWeight: active ? 700 : 400,
                  "&:hover": active ? undefined : { bgcolor: "action.hover" },
                }}
                role={active ? undefined : "button"}
                tabIndex={active ? -1 : 0}
                onClick={() => !active && handleRowClick(image)}
                onKeyDown={(e) => {
                  if (active) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleRowClick(image);
                  }
                }}
                data-testid={`datasetImageList.row.${kind}.${image.id}`}
                {...(active && { "data-active": "" })}
              >
                <Tooltip title={image.train ? t.TrainImage : t.TestImage} arrow>
                  {image.train ? (
                    <TrainIcon
                      fontSize="small"
                      sx={{
                        color: active ? "common.white" : "error.main",
                      }}
                      data-testid={`datasetImageList.splitIcon.train.${image.id}`}
                    />
                  ) : (
                    <ScienceIcon
                      fontSize="small"
                      sx={{
                        color: active ? "common.white" : "info.main",
                      }}
                      data-testid={`datasetImageList.splitIcon.test.${image.id}`}
                    />
                  )}
                </Tooltip>
                {hasTruePositive && (
                  <Tooltip title={t.ImageHasTruePositive} arrow>
                    <CheckCircleIcon
                      fontSize="small"
                      sx={{
                        color: active ? "common.white" : "success.main",
                      }}
                      data-testid={`datasetImageList.statusIcon.tp.${image.id}`}
                    />
                  </Tooltip>
                )}
                {hasFalsePositive && (
                  <Tooltip title={t.ImageHasFalsePositive} arrow>
                    <ThumbDownIcon
                      fontSize="small"
                      sx={{
                        color: active
                          ? "common.white"
                          : DEFECT_BOX_COLOR.falsePositive,
                      }}
                      data-testid={`datasetImageList.statusIcon.fp.${image.id}`}
                    />
                  </Tooltip>
                )}
                {objectWithoutDefect && (
                  <Tooltip title={t.ImageHasObjectWithoutDefect} arrow>
                    <BakeryDiningIcon
                      fontSize="small"
                      sx={{
                        color: active ? "common.white" : "success.light",
                      }}
                      data-testid={`datasetImageList.statusIcon.clean.${image.id}`}
                    />
                  </Tooltip>
                )}
                {image.modified_since_integration && (
                  <Tooltip title={t.ModifiedSinceIntegrationTooltip} arrow>
                    <EditNoteIcon
                      fontSize="small"
                      sx={{
                        color: active ? "common.white" : "warning.main",
                      }}
                      data-testid={`datasetImageList.statusIcon.modified.${image.id}`}
                    />
                  </Tooltip>
                )}
                <Typography
                  variant="body2"
                  sx={{ flex: 1, fontWeight: "inherit" }}
                >
                  {`${image.region.toUpperCase()}${image.id}`}
                </Typography>
                {seen && (
                  <Tooltip title={t.ImageSeenThisSession} arrow>
                    <CheckIcon
                      fontSize="small"
                      sx={{
                        color: active ? "common.white" : "text.secondary",
                      }}
                      data-testid={`datasetImageList.seenIcon.${image.id}`}
                    />
                  </Tooltip>
                )}
              </Stack>
            );
          }}
        />
      </Box>
    </Stack>
  );
}
